<script setup>
import { defineAsyncComponent, onMounted, ref, useTemplateRef } from 'vue'

const SearchAttributeSelect = defineAsyncComponent(() =>
  import('@/components/Generics/Filters/SearchAttributeSelect.vue')
)

import TranslationProvider from '@/provider/translations.js'
import { useFiltersStore } from '@/services/store.js'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faFilter, faInfoCircle } from '@digitalpa/pro-solid-svg-icons'
import ConfigurationProvider from '@/provider/configuration.js'
import { attr_gec_subject_vat, attr_gec_suppliers, attr_subject_fiscal_codes } from '@/services/compiledPollService.js'

library.add(faFilter, faInfoCircle)

const props = defineProps({
  isLoading: Boolean,
  pageType: {
    type: String,
    default: 'compiled-poll-list'
  },
  currentFilters: {
    default: '',
    type: String
  },
  workflowList: {
    default: []
  },
  statesList: {
    default: []
  }
})

let CustomFilters = null
if (ConfigurationProvider.getConfig()['app']['filters']['additional_filters_component']) {
  const folder = ConfigurationProvider.getConfig()['app']['filters']['additional_filters_component']['folder']
  const name = ConfigurationProvider.getConfig()['app']['filters']['additional_filters_component']['name']
  CustomFilters = defineAsyncComponent(() =>
    import(`@/components/${folder}/${name}.vue`)
  )
}
const customFilters = useTemplateRef('custom-filters')
const filtersStore = useFiltersStore()

const emits = defineEmits(['applicaFiltri', 'showCardChanged'])

const disabledResettaFiltri = ref(true)
const showCard = ref(true)
const loadingFilters = ref(true)
const activeFilters = ref(0)
const emptyAttributes = ref([])

const errorFilters = ref({
  enabled: false,
  message: ''
})

onMounted(async () => {
  loadingFilters.value = true
  errorFilters.value.enabled = false
  errorFilters.value.message = ''
  if (props.pageType === 'campaign-summary'){
    resettaFiltri()
  }
  showCard.value = await filtersStore.getShowCard()
  if (ConfigurationProvider.getConfig()['app']['filters']['empty_attributes']) {
    for (const emptyAttr of ConfigurationProvider.getConfig()['app']['filters']['empty_attributes']) {
      emptyAttributes.value.push({
        label: TranslationProvider.getTrans(emptyAttr.label),
        value: emptyAttr.value
      })
    }
  }
  countActiveFilters()
  loadingFilters.value = false
  checkDisabledResettaFiltri()
})

function checkDisabledResettaFiltri() {
  disabledResettaFiltri.value = true  // TODO - refactor con filterStore
  if (Object.keys(filtersStore.filters[props.pageType]).length) {
    Object.values(filtersStore.filters[props.pageType]).forEach(filter => {
      if (filter) {
        disabledResettaFiltri.value = false
      }
    })
  }
}

function resettaFiltri() {
  errorFilters.value.enabled = false
  errorFilters.value.message = ''
  if (props.pageType) {
    filtersStore.filters[props.pageType] = {}
  }else{
    filtersStore.resetFilters()
  }
  if (customFilters.value) {
    customFilters.value.resetFilters()
  }

  checkDisabledResettaFiltri()
  applicaFiltri()
}

function applicaFiltri() {
  errorFilters.value.enabled = false
  errorFilters.value.message = ''
  checkDisabledResettaFiltri()
  if (filtersStore.filters[props.pageType]['state']?.length && filtersStore.filters[props.pageType]['exclude_states']?.length) {
    errorFilters.value.message = TranslationProvider.getTrans('app.error_filters.excludeStates_with_state_filter')
    errorFilters.value.enabled = true
    return
  }
  emits('applicaFiltri')
  countActiveFilters()
}

const showHideFilters = () => {
  showCard.value = !showCard.value

  emits('showCardChanged', showCard.value)

  filtersStore.setShowCard(showCard.value)
}

const countActiveFilters = () => {
  activeFilters.value = 0
  const filters = filtersStore.filters[props.pageType]
  for (const [index, entry] of Object.entries(filters)) {
    if (index !== 'attribute_filters' && (Array.isArray(entry) || typeof entry !== 'object') && entry !== undefined && entry !== null && entry) {
      if ((Array.isArray(entry) && !entry.length) || (typeof entry == 'object' && !Object.values(entry).length)) {
        continue
      }
      activeFilters.value += 1

    }
  }
}
</script>

<template>
  <div
    class="col-12 p-3 mb-3  border-bottom"
    @keydown.enter="applicaFiltri"
  >
    <div class="container-fluid">

      <div class="row mb-3">
        <div class="col-12 text-end"
             v-if="
            ConfigurationProvider.getConfig()['app']['filters']['default_enabled'] ||
            ConfigurationProvider.getConfig()['app']['filters']['additional_filters_component']
          "
        >
          <button :disabled="loadingFilters" class="btn btn-sm btn-primary" @click="showHideFilters">
            <span v-if="loadingFilters" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
            <span v-if="loadingFilters" class="visually-hidden" role="status">Loading...</span>
            <span v-else class="fw-bold">
              <span v-if="activeFilters" class="badge rounded-pill bg-white text-primary">
                {{ activeFilters }}
              </span>
              {{ TranslationProvider.getTrans('app.filters') }}
              <font-awesome-icon class="ms-2" :icon="'fa-filter'"></font-awesome-icon>
            </span>
          </button>
        </div>
      </div>
      <div :class="'row collapse ' + (showCard && !loadingFilters ? 'show' : '')">
        <div class="col-12 px-1" v-if="errorFilters.enabled">
          <div class="alert alert-danger px-2 py-2" v-html="errorFilters.message" />
        </div>
        <template v-if="ConfigurationProvider.getConfig()['app']['filters']['default_enabled']">

          <div class="col-12 col-lg-3 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('fornitore')"
          >
            <div class="mb-2">
              <SearchAttributeSelect
                :label="TranslationProvider.getTrans('app.fornitore')"
                :attribute="attr_gec_suppliers"
                :page-type="props.pageType"
              ></SearchAttributeSelect>
            </div>
          </div>

          <div class="col-12 col-lg-3 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('partita_iva')"
          >
            <div class="mb-2">
              <SearchAttributeSelect
                :label="TranslationProvider.getTrans('app.partita_iva')"
                :attribute="attr_gec_subject_vat"
                :page-type="props.pageType"
              ></SearchAttributeSelect>
            </div>
          </div>

          <div class="col-12 col-lg-3 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('codice_fiscale')"
          >
            <div class="mb-2">
              <SearchAttributeSelect
                :label="TranslationProvider.getTrans('app.codice_fiscale')"
                :attribute="attr_subject_fiscal_codes"
                :page-type="props.pageType"
              ></SearchAttributeSelect>
            </div>
          </div>

          <div class="col-12 col-lg-3 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('titoloCampagna')"
          >
            <div class="mb-2">
              <label for="descrizione_risorsa_standard" class="form-label">
                {{ TranslationProvider.getTrans('app.campagna') }}
              </label>
              <input
                id="titoloCampagna"
                v-model="filtersStore.filters[props.pageType]['titolo_campagna']"
                type="text"
                class="form-control form-control-sm"
                :placeholder="TranslationProvider.getTrans('app.campagna')"
              />
            </div>
          </div>

          <div class="col-12 col-lg-3 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('cod_commessa')"
          >
            <div class="mb-2">
              <label for="cod_commessa" class="form-label">
                {{ TranslationProvider.getTrans('app.codice_commessa') }}
              </label>
              <input
                id="cod_commessa"
                v-model="filtersStore.filters[props.pageType]['gec_order_uuid']"
                type="text"
                class="form-control form-control-sm"
                :placeholder="TranslationProvider.getTrans('app.codice_commessa')"
              />
            </div>
          </div>

          <div class="col-12 col-lg-3 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('cod_contratto')"
          >
            <div class="mb-2">
              <label for="cod_contratto" class="form-label">
                {{ TranslationProvider.getTrans('app.codice_contratto') }}
              </label>
              <input
                id="cod_contratto"
                v-model="filtersStore.filters[props.pageType]['gec_uuid']"
                type="text"
                class="form-control form-control-sm"
                :placeholder="TranslationProvider.getTrans('app.codice_contratto')"
              />
            </div>
          </div>

          <div class="col-12 col-lg-3 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('state')"
          >
            <div class="mb-2">
              <label for="state" class="form-label">
                {{ TranslationProvider.getTrans('app.stato_somministrazione') }}
              </label>
              <v-select
                id="state"
                v-model="filtersStore.filters[props.pageType]['state']"
                :options="statesList"
                :reduce="state => state.name"
                label="label"
                :placeholder="TranslationProvider.getTrans('app.stato_somministrazione')"
              >
                <template #no-options>
                  <div style="opacity: 0.8">{{ TranslationProvider.getTrans('app.no_result_found') }}</div>
                </template>
              </v-select>
            </div>
          </div>

          <div class="col-12 col-lg-3 px-2"
               v-if="ConfigurationProvider.getConfig()['app']['filters']['exclude_states']"
          >
            <div class="mb-2">
              <label for="state" class="form-label">
                {{ TranslationProvider.getTrans('app.questionari_da_escludere') }}
              </label>
              <v-select
                id="state"
                v-model="filtersStore.filters[props.pageType]['exclude_states']"
                :options="statesList"
                :reduce="state => state.name"
                label="label"
                multiple
                :clearSearchOnBlur="() => true"
                :placeholder="TranslationProvider.getTrans('app.questionari_da_escludere')"
              >
                <template #no-options>
                  <div style="opacity: 0.8">{{ TranslationProvider.getTrans('app.no_result_found') }}</div>
                </template>
              </v-select>
            </div>
          </div>

          <div class="col-12 col-lg-6 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('titolo_contratto')"
          >
            <div class="mb-2">
              <label for="titolo_contratto" class="form-label">
                {{ TranslationProvider.getTrans('app.filter.oggetto') }}
              </label>
              <input
                id="titolo_contratto"
                v-model="filtersStore.filters[props.pageType]['gec_contract_object']"
                type="text"
                class="form-control form-control-sm"
                :placeholder="TranslationProvider.getTrans('app.filter.oggetto')"
              />
            </div>
          </div>

          <div class="col-12 col-lg-3 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('empty_attribute') && emptyAttributes.length"
          >
            <div class="mb-2">
              <label for="empty_attribute" class="form-label">
                {{ TranslationProvider.getTrans('app.empty_attribute') }}
              </label>
              <v-select
                id="empty_attribute"
                v-model="filtersStore.filters[props.pageType]['empty_attribute']"
                :options="emptyAttributes"
                :reduce="attr => attr.value"
                :placeholder="TranslationProvider.getTrans('app.empty_attribute')"
                multiple
                clearable
                :clearSearchOnBlur="() => true"
              >
                <template #no-options>
                  <div style="opacity: 0.8">{{ TranslationProvider.getTrans('app.no_result_found') }}</div>
                </template>
              </v-select>
            </div>
          </div>

          <div class="col-12 col-lg-3 px-2"
               v-if="!ConfigurationProvider.getConfig()['app']['filters']['exclude_defaults'].includes('workflow')"
          >
            <div class="mb-2">
              <label for="empty_attribute" class="form-label">
                {{ TranslationProvider.getTrans('app.workflow') }}
              </label>
              <v-select
                id="workflow"
                v-model="filtersStore.filters[props.pageType]['workflow']"
                :options="workflowList"
                :reduce="attr => attr.key"
                :placeholder="TranslationProvider.getTrans('app.workflow')"
                label="label"
                clearable
                :clearSearchOnBlur="() => true"
              >
                <template #no-options>
                  <div style="opacity: 0.8">{{ TranslationProvider.getTrans('app.no_result_found') }}</div>
                </template>
              </v-select>
            </div>
          </div>
        </template>

        <component
          v-if="ConfigurationProvider.getConfig()['app']['filters']['additional_filters_component']"
          :is="CustomFilters"
          :type="props.pageType"
          ref="custom-filters"
        >
        </component>
      </div>


      <div class="col-12 d-flex justify-content-end mt-2 px-2">
        <button
          class="btn btn-secondary btn-sm mx-1"
          :disabled="disabledResettaFiltri"
          @click="resettaFiltri"
        >
          {{ TranslationProvider.getTrans('app.reset_filters') }}
        </button>
        <button
          class="btn btn-primary btn-sm mx-1"
          :disabled="isLoading"
          @click="applicaFiltri"
        >
          {{ TranslationProvider.getTrans('app.apply_filters') }}
        </button>
      </div>
    </div>

  </div>
</template>

<style lang="scss">
.container-fluid .row {

  & .vs__search, .vs__selected {
    font-size: 0.875rem !important;
    margin: 2px 3px 2px 4px !important;
  }

  & .vs__search::placeholder {
    opacity: 0.8 !important;
  }

  & .vs__clear {
    font-size: 0.7rem !important;
  }
}
</style>
